import { useStatusStore } from "../../../../utils/stores/useStatusStore";
import "./ClientOverview.scss";
import {
  IconFileImport,
  IconUserExclamation,
  IconUserScan,
} from "@tabler/icons-react";
import ReusableSkeleton from "../../../../components/ReusableSkeleton/ReusableSkeleton";
import { Grid, Skeleton } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { useTaxProAnalytics } from "../../../../utils/hooks/useAnalytics";
import { AppRoutes } from "@/constants.ts";

const ClientOverview = () => {
  const { statuses, totalClients, isLoadingAnalytics, isErrorAnalytics } =
    useTaxProAnalytics();

  const { setReadyToGo, setClickedStatus, setClickedAssignees } =
    useStatusStore();

  const navigate = useNavigate();

  if (isErrorAnalytics) {
    throw new Error("Error fetching analytics");
  }

  const info = [
    {
      id: 0,
      title: "Total Clients",
      icon: <IconUserScan />,
      value: totalClients || 0,
    },
    {
      id: 1,
      title: "New Clients",
      icon: <IconUserExclamation />,
      value: statuses?.New,
    },
    {
      id: 2,
      title: "Ready to go",
      description: "Ready to be filed",
      icon: <IconFileImport />,
      value: statuses?.["Ready To Go"],
    },
  ];

  const customSkeletons = (
    <Grid columns={24} gutter="md" mt="100">
      {info.map((item) => (
        <Grid.Col span={8} key={item.id}>
          <Skeleton height={100} radius="sm" />
        </Grid.Col>
      ))}
    </Grid>
  );

  const resetFilters = () => {
    setReadyToGo(false); // Reset the filter
    setClickedStatus(""); // Reset the filter
    setClickedAssignees([]);
  };

  const handleReadyToGoClick = () => {
    resetFilters();
    setClickedStatus("Ready To Go");
    navigate(AppRoutes.clients);
  };

  const handleNewClientsClick = () => {
    resetFilters();
    setClickedStatus("New");
    navigate(AppRoutes.clients);
  };

  const handleTotalClientsClick = () => {
    resetFilters();
    navigate(AppRoutes.clients);
  };

  const handleClick = (title: string) => {
    if (title === "Ready to go") {
      return handleReadyToGoClick;
    } else if (title === "New Clients") {
      return handleNewClientsClick;
    } else {
      return handleTotalClientsClick;
    }
  };

  return (
    <div className="client-overview">
      <ReusableSkeleton
        isLoading={isLoadingAnalytics}
        customSkeletons={customSkeletons}
      >
        {info.map((item: any) => (
          <button
            className={`client-overview-item ${
              item.title === "Ready to go" ? "ready-to-go" : ""
            }`}
            key={item.id}
            onClick={handleClick(item.title)}
          >
            <div className="client-overview-item-content">
              <span className="client-overview-title">
                {item.icon}
                {item.title}
              </span>
              {/* if item has no description, preserve the space for layout */}
              <p className="description">{item?.description || "\u00A0"}</p>
            </div>
            <p>{item.value}</p>
          </button>
        ))}
      </ReusableSkeleton>
    </div>
  );
};

export default ClientOverview;
